import classNames from "classnames";
import type { WovarProduct } from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { FC } from "react";
import { buildProductSlides, productSlideKey } from "~/lib/productSlides";
import type { ProductGalleryVideo } from "~/lib/youtubeUtils";
import WvProductCarouselSlide from "../molecules/WvProductCarouselSlide";
import WvProductSlideThumbnail from "../molecules/WvProductSlideThumbnail";

const WvProductSwiper: HTMLComponent<{
  images: WovarProduct["images"];
  /** Product API videos — YouTube links + direct files (MP4 etc.) via `buildProductGalleryVideos`. */
  videos?: ProductGalleryVideo[];
  onPreview?(index: number): void;
  marketingSticker?: string;
  isPreview?: boolean;
  onClose?: Function;
  embla: any;
}> = ({
  images,
  videos,
  onPreview,
  marketingSticker,
  isPreview,
  onClose,
  embla,
}) => {
  const {
    emblaRef: mainViewportRef,
    scrollTo,
    scrollPrev,
    scrollNext,
    selectedIndex: productIndex,
  } = embla;

  useEffect(() => {
    scrollTo(0);
    const handleKeypress = (key: string) => {
      switch (key) {
        case "Escape":
          onClose?.();
          break;
        case "ArrowRight":
          document.getElementById("preview-next")?.click();
          break;
        case "ArrowLeft":
          document.getElementById("preview-prev")?.click();
          break;
        default:
          break;
      }
    };
    const eventListner = (e: any) => {
      if (e.repeat) e.preventDefault();
      handleKeypress(e.key);
    };
    if (isPreview) window.addEventListener("keyup", eventListner);

    return () => {
      window.removeEventListener("keyup", eventListner);
    };
  }, []);

  const [thumbnailsCount, setThumbnailCount] = useState(3);

  const slides = useMemo(
    () => buildProductSlides(images, videos),
    [images, videos]
  );

  const imageOptions = useMemo(
    () => slides.slice(0, thumbnailsCount),
    [slides, thumbnailsCount]
  );

  /** Single cell to measure — one ref on many nodes only keeps the last node, which broke layout (e.g. last YouTube thumb). */
  const firstImageThumbIndex = useMemo(() => {
    const idx = imageOptions.findIndex((slide) => slide.kind === "image");
    return idx === -1 ? 0 : idx;
  }, [imageOptions]);

  const optionOverflow = useMemo(
    () => slides.length - thumbnailsCount,
    [slides, thumbnailsCount]
  );

  const wrapperClasses = (index: number) => {
    return classNames("product-viewport__img-wrapper--options", {
      "product-viewport__img-wrapper--active": productIndex === index,
      "product-viewport__img-wrapper--inactive":
        index > thumbnailsCount - 2 && slides.length > thumbnailsCount,
    });
  };

  const optionOverflowLabel = (index: number) => {
    return index > thumbnailsCount - 2 && slides.length > thumbnailsCount ? (
      <span
        onClick={() => {
          onPreview?.(thumbnailsCount - 1);
        }}
        className="product-viewport__count"
      >
        +{optionOverflow}
      </span>
    ) : null;
  };

  const StickerLabel: FC<{ title: string | undefined }> = ({ title }) =>
    title ? (
      <div className="productblock__toplabel" data-icon-before="">
        {title}
      </div>
    ) : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const imgWrapperRef = useRef<HTMLDivElement>(null);

  const evaluateVisibleThumbnailsCount = () => {
    const container_width = containerRef?.current?.offsetWidth;
    const img_width = imgWrapperRef?.current?.offsetWidth || 74;
    // +15px gap between the images
    setThumbnailCount((prevCount) => {
      return container_width
        ? Math.floor(container_width / (img_width + 15))
        : prevCount;
    });
  };

  useEffect(() => {
    evaluateVisibleThumbnailsCount();
    window.addEventListener("resize", evaluateVisibleThumbnailsCount);
    return () => {
      window.removeEventListener("resize", evaluateVisibleThumbnailsCount);
    };
  }, [slides]);

  const totalSlideCount = slides.length;

  return (
    <React.Fragment>
      <div className="product-swiper-wrapper">
        <StickerLabel title={marketingSticker} />
        {totalSlideCount > 1 && (
          <div className={isPreview ? "lg-actions" : ""}>
            <button
              id={isPreview ? "preview-prev" : undefined}
              data-icon-before=""
              className={
                isPreview
                  ? "lg-prev lg-icon"
                  : "product-viewport__nav-icon product-viewport__nav-icon--left"
              }
              onClick={() => {
                scrollPrev();
              }}
            ></button>

            <button
              id={isPreview ? "preview-next" : undefined}
              data-icon-after=""
              className={
                isPreview
                  ? "lg-next lg-icon"
                  : "product-viewport__nav-icon product-viewport__nav-icon--right"
              }
              onClick={() => {
                scrollNext();
              }}
            ></button>
          </div>
        )}

        <div
          className={"product-viewport".concat(isPreview ? " border-none" : "")}
          ref={mainViewportRef}
          onClick={() => {
            onPreview?.(productIndex);
          }}
        >
          <div className="product-viewport__container">
            {slides.map((slide, index) => (
              <WvProductCarouselSlide
                key={productSlideKey(slide, index)}
                slide={slide}
                active={productIndex === index}
                priority={!isPreview && index === 0}
              />
            ))}
          </div>
        </div>
      </div>

      {!isPreview && totalSlideCount > 1 ? (
        <div className="product-viewport--options">
          <div className="product-viewport__container" ref={containerRef}>
            {imageOptions.map((slide, index) => (
              <div
                ref={index === firstImageThumbIndex ? imgWrapperRef : undefined}
                className={wrapperClasses(index)}
                onClick={() => {
                  scrollTo(index);
                }}
                key={productSlideKey(slide, index)}
              >
                <WvProductSlideThumbnail slide={slide} />
                {optionOverflowLabel(index)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-4"></div>
      )}
    </React.Fragment>
  );
};

export default WvProductSwiper;
